import type { Settlement } from '../settlements/types';

export interface EarningsCycle {
  /** Minor units (kobo/cents). */
  gross: number;
  /** Minor units (kobo/cents). */
  net: number;
  settlementCount: number;
}

export interface EarningsSummary {
  currency: string;
  /** Settlements settled within the current calendar month. */
  current: EarningsCycle;
  /** Settlements settled within the previous calendar month. */
  previous: EarningsCycle;
  allTimeNet: number;
  /** Most recent settlement by settledAt (nulls last); may be any status. */
  latestSettlement: Settlement | null;
}

function emptyCycle(): EarningsCycle {
  return { gross: 0, net: 0, settlementCount: 0 };
}

function isSameYearMonth(date: Date, year: number, month: number): boolean {
  return date.getFullYear() === year && date.getMonth() === month;
}

/**
 * Compute an earnings summary from a list of settlements.
 *
 * @param settlements - All settlements for the merchant.
 * @param now - Reference date (defaults to new Date()). Used for cycle boundaries.
 */
export function computeEarnings(
  settlements: Settlement[],
  now: Date = new Date()
): EarningsSummary {
  const currency = settlements[0]?.currency ?? 'NGN';

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed

  const prevDate = new Date(currentYear, currentMonth - 1, 1);
  const prevYear = prevDate.getFullYear();
  const prevMonth = prevDate.getMonth();

  const current = emptyCycle();
  const previous = emptyCycle();
  let allTimeNet = 0;

  // Determine most recent by settledAt (nulls last)
  let latestSettlement: Settlement | null = null;

  for (const s of settlements) {
    // Track latest by settledAt regardless of status
    if (s.settledAt !== null) {
      if (
        latestSettlement === null ||
        latestSettlement.settledAt === null ||
        new Date(s.settledAt) > new Date(latestSettlement.settledAt)
      ) {
        latestSettlement = s;
      }
    } else if (latestSettlement === null) {
      // null settledAt only wins if no dated settlement has been seen
      latestSettlement = s;
    }

    // Only SUCCESS settlements count toward money sums
    if (s.status !== 'SUCCESS') continue;

    allTimeNet += s.amount;

    if (s.settledAt === null) continue;

    const settledDate = new Date(s.settledAt);

    if (isSameYearMonth(settledDate, currentYear, currentMonth)) {
      // gross == net at the settlement layer under split-at-source; platform commission is deducted upstream
      current.gross += s.amount;
      current.net += s.amount;
      current.settlementCount += 1;
    } else if (isSameYearMonth(settledDate, prevYear, prevMonth)) {
      // gross == net at the settlement layer under split-at-source; platform commission is deducted upstream
      previous.gross += s.amount;
      previous.net += s.amount;
      previous.settlementCount += 1;
    }
  }

  // If we only found a null-settledAt settlement as "latest" but there are also dated ones, prefer dated
  // (The loop above already handles this correctly — dated settlements replace null-only placeholder.)

  return {
    currency,
    current,
    previous,
    allTimeNet,
    latestSettlement,
  };
}
